import type { Health } from '@schwarz/contract';

import { useGetHealthQuery } from '../store/health-api.js';
import styles from './ConnectionStatus.module.css';

/**
 * The top bar's connection indicator (S03: "a real connection-status indicator").
 *
 * Real means it reflects an actual probe, not a hard-coded dot. It polls our
 * /health, which aggregates the database and the identity provider — the browser
 * never talks to Sirma directly, since it has no API key and must not be given one.
 *
 * Degraded states are distinguished by label as well as colour, so the indicator
 * does not depend on colour perception alone.
 *
 * ## Why it says nothing when everything is fine
 *
 * "Connected" is the state the console is in essentially always, and a label that
 * is permanently on screen stops being read — so when it eventually says
 * something else, nobody notices. It is silent while healthy and appears only
 * when there is something to say, which is the only condition under which an
 * indicator like this earns its place in the bar.
 *
 * The probe still runs on the same fifteen-second poll: the point is to say
 * nothing, not to know nothing.
 */
export function ConnectionStatus(): JSX.Element | null {
  const { data, isError, isLoading } = useGetHealthQuery(undefined, {
    pollingInterval: 15_000,
  });

  const { tone, label, detail } = describe({ data, isError, isLoading });

  // `idle` is the first load, before the first probe has answered. Also silent:
  // a "Checking…" that flashes on every page load is noise about our own
  // startup rather than news about the connection.
  if (tone === 'ok' || tone === 'idle') return null;

  return (
    <div className={`${styles.wrap} ${styles[tone]}`} title={detail}>
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.label}>{label}</span>
      <span className="sr-only">{detail}</span>
    </div>
  );
}

function describe(state: { data: Health | undefined; isError: boolean; isLoading: boolean }): {
  tone: 'ok' | 'warn' | 'bad' | 'idle';
  label: string;
  detail: string;
} {
  if (state.isLoading) return { tone: 'idle', label: 'Checking…', detail: 'Checking connection' };

  // A failed health request is itself the answer: if the API cannot be reached,
  // nothing else on the page is trustworthy either.
  if (state.isError || !state.data) {
    return { tone: 'bad', label: 'Offline', detail: 'Cannot reach the console API' };
  }

  const { status, checks } = state.data;
  if (status === 'down') {
    return { tone: 'bad', label: 'Degraded', detail: `Database ${checks.database}` };
  }
  if (status === 'degraded') {
    return {
      tone: 'warn',
      label: 'Degraded',
      detail: `Identity provider ${checks.idp}, Sirma AI ${checks.sirma}`,
    };
  }
  return {
    tone: 'ok',
    label: 'Connected',
    detail: `Database ok · identity provider ${checks.idp} · Sirma AI ${checks.sirma}`,
  };
}
